import { FC } from 'react';
import classNames from 'classnames';
import styles from './styles.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../../../../redux/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faLock, faPlus } from '@fortawesome/free-solid-svg-icons';
import HoverDisplayControlLayer from '../../../../../components/common/HoverDisplayControlLayer';
import {
  AllocationTask,
  AllocationTaskInEdit,
  getTimelineConstraint,
  isPairTask,
  isSameTask,
} from '../allocationTableUtility';
import { useDrag, useDrop } from 'react-dnd';
import {
  setEditTask,
  setSingleTaskReadyToUpdate,
  setStartOrArrivalTasksToReadyToUpdate,
} from '../../../../../redux/carAllocationEditSlice';
import { addMinutes, isEqual } from 'date-fns';
import { getWorkCategoryIDByName } from '../../../../../services/workCategory';
import { isTaskStartOrArrival } from '../../../../../services/isTaskStartOrArrival';

type Props = {
  workStartTime?: Date;
  workEndTime?: Date;
  restStartTime?: Date;
  restEndTime?: Date;
  currentTime: Date;
  carId?: number;
  userId?: number;
  allocations: AllocationTask[];
  isAvailable?: boolean;
  onClick?: (elem: HTMLElement) => void;
  onClickAddButton?: (elem: HTMLElement) => void;
  allowClickAddButtonBefore?: Date;
  allowClickAddButtonAfter?: Date;
  allowedDragAndDrop?: boolean;
  targetAllocation?: AllocationTaskInEdit;
  pairedTargetAllocation?: AllocationTaskInEdit;
};
const dndToken = 'allocation' as const;
const getAllocationCellStyle = (allocation?: {
  workCategory: {
    id: number;
  };
}) => {
  if (!allocation) {
    return '';
  }

  switch (allocation.workCategory.id) {
    case getWorkCategoryIDByName('出発'):
      return styles.departure;
    case getWorkCategoryIDByName('到着'):
      return styles.arrival;
    case getWorkCategoryIDByName('回収'):
      return styles.collect;
    case getWorkCategoryIDByName('積込'):
      return styles.loading;
    case getWorkCategoryIDByName('処分'):
      return styles.dispose;
    case getWorkCategoryIDByName('保管'):
      return styles.storage;
    default:
      return '';
  }
};
type TimeRange = {
  start: Date;
  end: Date;
};
const doesCurrentCellHaveAllocation = (
  workTimes: TimeRange,
  cellTimes: TimeRange
) => {
  const isCellTimesIncluded =
    workTimes.start <= cellTimes.start && cellTimes.end < workTimes.end;
  const doesCellContainsWorkTimeStartEdge =
    cellTimes.start <= workTimes.start && workTimes.start < cellTimes.end;
  // タスク終端が次のセルの始端と一致する場合は含めない
  const doesCellContainsWorkTimeEndEdge =
    cellTimes.start < workTimes.end && workTimes.end < cellTimes.end;
  return (
    isCellTimesIncluded ||
    doesCellContainsWorkTimeStartEdge ||
    doesCellContainsWorkTimeEndEdge
  );
};
const TimeCellData: FC<Props> = ({
  workStartTime,
  workEndTime,
  restStartTime,
  restEndTime,
  currentTime,
  carId,
  userId,
  allocations,
  isAvailable = true,
  onClick,
  onClickAddButton,
  allowClickAddButtonBefore,
  allowClickAddButtonAfter,
  allowedDragAndDrop,
  targetAllocation,
  pairedTargetAllocation,
}) => {
  const beforeTime = new Date(currentTime);
  beforeTime.setMinutes(currentTime.getMinutes() - 30);
  const nextTime = new Date(currentTime);
  nextTime.setMinutes(currentTime.getMinutes() + 30);
  const inWork =
    !!workStartTime &&
    !!workEndTime &&
    workStartTime <= currentTime &&
    currentTime < workEndTime;
  const isWorkStartEdge = !!workStartTime && beforeTime < workStartTime;
  const isWorkEndEdge = !!workEndTime && workEndTime <= nextTime;
  const cellSharedStyles = [
    styles.cell,
    isWorkStartEdge && styles.workStartEdge,
    isWorkEndEdge && styles.workEndEdge,
  ];
  const inEditMode = useSelector(
    (state: AppState) => state.carAllocationEdit.editMode
  );
  const currentAllocation = allocations.find((t) =>
    doesCurrentCellHaveAllocation(
      {
        start: new Date(t.startDateForPlacement),
        end: new Date(t.endDateForPlacement),
      },
      {
        start: currentTime,
        end: nextTime,
      }
    )
  );
  const allowedClickAddButtonBefore = allowedDragAndDrop
    ? !allowClickAddButtonBefore || currentTime < allowClickAddButtonBefore
    : allowClickAddButtonBefore
    ? currentTime < allowClickAddButtonBefore
    : workEndTime && currentTime < workEndTime;
  const allowedClickAddButtonAfter = allowedDragAndDrop
    ? !allowClickAddButtonAfter || currentTime >= allowClickAddButtonAfter
    : allowClickAddButtonAfter
    ? currentTime >= allowClickAddButtonAfter
    : workStartTime && currentTime >= workStartTime;
  const isInRestTime =
    !!restStartTime &&
    !!restEndTime &&
    restStartTime <= currentTime &&
    currentTime < restEndTime;
  const allowedClickAddButton =
    allowedClickAddButtonBefore && allowedClickAddButtonAfter;

  const dispatch = useDispatch<AppDispatch>();
  const allEditAllocations = useSelector(
    (state: AppState) => state.carAllocationEdit.allocationTasks
  );
  const canDrop = inWork || (!workStartTime && !workEndTime);
  const [dropProps, drop] = useDrop<
    AllocationTask,
    unknown,
    { undroppable: boolean }
  >({
    accept: dndToken,
    canDrop(item) {
      return canDrop || isTaskStartOrArrival(item);
    },
    drop(item) {
      const secondaryTask = allEditAllocations
        .filter((a) => a.carId === item.carId && a.userId === item.userId)
        .find((a) => {
          return isPairTask(a, item);
        });
      const newStartDate = currentTime.toISOString();
      const newEndDate = addMinutes(
        currentTime,
        item.workCategory.estimatedTime
      ).toISOString();
      if (
        isTaskStartOrArrival(item) &&
        carId === item.carId &&
        userId === item.userId
      ) {
        dispatch(
          setStartOrArrivalTasksToReadyToUpdate({
            ...item,
            startDate: newStartDate,
            startDateForPlacement: newStartDate,
            endDate: newEndDate,
            endDateForPlacement: newEndDate,
          })
        );
      } else if (item.isMergeTask) {
        dispatch(
          setSingleTaskReadyToUpdate({
            ...item,
            startDate: newStartDate,
            startDateForPlacement: newStartDate,
            endDate: newEndDate,
            endDateForPlacement: newEndDate,
          })
        );
      } else if (secondaryTask) {
        dispatch(
          setEditTask({
            type: item.type,
            primary: {
              ...item,
              startDate: newStartDate,
              startDateForPlacement: newStartDate,
              endDate: newEndDate,
              endDateForPlacement: newEndDate,
              carId,
              userId,
            },
            secondary: {
              ...secondaryTask,
              startDate: undefined,
              startDateForPlacement: undefined,
              endDate: undefined,
              endDateForPlacement: undefined,
              carId,
              userId,
            },
            targetCarId: carId,
            targetUserId: userId,
            step: 2,
            isMoving: true,
          })
        );
      }
    },
    collect(monitor) {
      const item = monitor.getItem();
      if (!monitor.isOver()) {
        return { undroppable: false };
      }
      if (isTaskStartOrArrival(item)) {
        return {
          undroppable: !(carId === item.carId && userId === item.userId),
        };
      }

      const constraint = getTimelineConstraint(item, allocations);
      const inLaterConstraintRange =
        !constraint.later || currentTime >= constraint.later;
      const inEarlierConstraintRnage =
        !constraint.earlier || currentTime < constraint.earlier;
      const verticalDroppable =
        item.isEditable.vertical ||
        (carId === item.carId && userId === item.userId);
      const horizontalDroppable =
        item.isEditable.horizontal ||
        isEqual(currentTime, new Date(item.startDateForPlacement));
      return {
        undroppable:
          !canDrop ||
          !(
            verticalDroppable &&
            horizontalDroppable &&
            inLaterConstraintRange &&
            inEarlierConstraintRnage
          ),
      };
    },
  });
  const [dragProps, drag] = useDrag(
    () => ({
      type: dndToken,
      item: currentAllocation,
      collect(monitor) {
        return {
          isDragging: monitor.isDragging(),
        };
      },
    }),
    [currentAllocation]
  );

  const addButon = (
    <button
      className={styles.addButton}
      onClick={(e) => {
        e.stopPropagation();
        onClickAddButton?.(e.currentTarget);
      }}
    >
      <FontAwesomeIcon icon={faPlus} />
    </button>
  );
  const inWorkItem = (
    <>
      <HoverDisplayControlLayer
        className={classNames(
          ...cellSharedStyles,
          !useSelector(
            (state: AppState) => state.carAllocationEdit.editState
          ) && styles.inWork,
          dragProps.isDragging && styles.dragging
        )}
        ref={drop}
      >
        {inEditMode && allowedClickAddButton && addButon}
      </HoverDisplayControlLayer>
      <UndroppableIndicator undroppable={dropProps.undroppable} />
    </>
  );
  const emptyItem = (
    <>
      <HoverDisplayControlLayer
        className={classNames(
          ...cellSharedStyles,
          dragProps.isDragging && styles.dragging
        )}
        ref={drop}
      >
        {inEditMode &&
          allowedClickAddButton &&
          !workStartTime &&
          !workEndTime &&
          addButon}
      </HoverDisplayControlLayer>
      <UndroppableIndicator undroppable={dropProps.undroppable} />
    </>
  );
  const dayOffItem = (
    <div className={classNames(...cellSharedStyles, styles.dayOff)}></div>
  );

  if (!currentAllocation) {
    if (
      (inEditMode && !allowedClickAddButton) ||
      isInRestTime ||
      !isAvailable
    ) {
      return dayOffItem;
    }
    return <>{inWork ? inWorkItem : emptyItem}</>;
  }

  // 追加しようとしている配車タスクと同じ既存タスクかどうか
  const isTheSameAsTarget =
    !!targetAllocation &&
    isSameTask(currentAllocation, targetAllocation) &&
    currentAllocation.startDate !== targetAllocation.startDate;
  // 追加しようとしている配車タスクそのものかどうか
  const isTarget =
    !!targetAllocation &&
    isSameTask(currentAllocation, targetAllocation) &&
    currentAllocation.startDate === targetAllocation.startDate;
  // 追加しようとしている配車タスクに付随するタスクと同じ既存タスクかどうか
  const isTheSameAsPairedTarget =
    !!pairedTargetAllocation &&
    // 現在のセルのタスクが「付随するタスク」と同じかどうかを判定したいので isPairTask ではなく isSameTask を使う必要がある
    isSameTask(currentAllocation, pairedTargetAllocation) &&
    currentAllocation.startDate !== pairedTargetAllocation.startDate;
  // 追加しようとしている配車タスクに付随するタスクそのものかどうか
  const isPairedTarget =
    !!pairedTargetAllocation &&
    // 現在のセルのタスクが「付随するタスク」と同じかどうかを判定したいので isPairTask ではなく isSameTask を使う必要がある
    isSameTask(currentAllocation, pairedTargetAllocation) &&
    currentAllocation.startDate === pairedTargetAllocation.startDate;
  const taskStartDate = new Date(currentAllocation.startDate);
  const isTaskStartEdge =
    currentTime <= taskStartDate && taskStartDate < nextTime;
  const taskEndDate = addMinutes(
    taskStartDate,
    currentAllocation.workCategory.estimatedTime
  );
  // task 終了の端はジャストになる可能性があるため同値を許容する
  const isTaskEndEdge = currentTime <= taskEndDate && taskEndDate <= nextTime;
  return (
    <>
      <div
        className={classNames(
          ...cellSharedStyles,
          getAllocationCellStyle(currentAllocation),
          inEditMode && !currentAllocation.isLocked && styles.draggable,
          inEditMode &&
            !currentAllocation.isEditable.vertical &&
            !currentAllocation.isEditable.horizontal &&
            styles.nonEditable,
          dragProps.isDragging && styles.dragging,
          (isTheSameAsTarget || isTheSameAsPairedTarget) && styles.sameAsTarget,
          (isTarget || isPairedTarget) && styles.target,
          isTaskStartEdge && styles.taskStartEdge,
          isTaskEndEdge && styles.taskEndEdge
        )}
        onClick={(event) => {
          if (inEditMode) {
            return;
          }
          event.stopPropagation();
          onClick?.(event.currentTarget);
        }}
        ref={
          allowedDragAndDrop &&
          !currentAllocation.isLocked &&
          (currentAllocation.isEditable.vertical ||
            currentAllocation.isEditable.horizontal)
            ? drag
            : undefined
        }
      >
        <span className={styles.text}>
          {isTaskStartEdge && currentAllocation?.workCategory.name}
        </span>
        {currentAllocation.isLocked && (
          <div className={styles.lock}>
            <FontAwesomeIcon icon={faLock} />
          </div>
        )}
        {(isTheSameAsTarget || isTheSameAsPairedTarget) &&
          allowedClickAddButton && (
            <HoverDisplayControlLayer className={styles.editLayer}>
              {addButon}
            </HoverDisplayControlLayer>
          )}
      </div>
      <UndroppableIndicator undroppable={dropProps.undroppable} />
    </>
  );
};

type UndroppableIndicatorProps = { undroppable: boolean };
const UndroppableIndicator: FC<UndroppableIndicatorProps> = ({
  undroppable,
}) => {
  return (
    <>
      {undroppable && (
        <div className={styles.undroppable}>
          <FontAwesomeIcon icon={faBan}></FontAwesomeIcon>
        </div>
      )}
    </>
  );
};

export default TimeCellData;
